import { useCallback, useState } from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderInfo,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../components/BottomActionSheet";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import { RecurringCardMenu } from "./RecurringCardMenu";
import { KebabButton } from "../../../../components/KebabButton";
import {
  Amount,
  Card,
  CardFooter,
  CardHeader,
  CardHeaderRight,
  CardTitle,
  CountdownPill,
  DateBadge,
  DateDay,
  DateDivider,
  DateMonth,
  Frequency,
  MenuWrapper,
} from "./styles";
import { RecurringCardProps } from "./types";
import {
  formatSignedAmount,
  getAmountVariant,
  getCardTitle,
  getCountdown,
  getDayBadge,
  getFrequencyLabel,
} from "./utils";

export const RecurringCard: React.FC<RecurringCardProps> = ({
  transaction,
  onEdit,
  onDelete,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const variant = getAmountVariant(transaction.transactionType);
  const badge = getDayBadge(transaction.nextExecutionDate);
  const countdown = getCountdown(transaction.nextExecutionDate);
  const frequencyLabel = getFrequencyLabel(
    transaction.frequency,
    transaction.interval,
  );
  const title = getCardTitle(transaction);
  const amount = formatSignedAmount(transaction.amount, variant);
  const sheetSubtitle = `${frequencyLabel} · ${badge.day} ${badge.month}`;

  const handleEdit = useCallback(() => {
    onEdit(transaction.id);
  }, [onEdit, transaction.id]);

  const handleDelete = useCallback(() => {
    onDelete(transaction);
  }, [onDelete, transaction]);

  const openSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const onSheetEdit = useCallback(() => {
    closeSheet();
    handleEdit();
  }, [closeSheet, handleEdit]);

  const onSheetDelete = useCallback(() => {
    closeSheet();
    handleDelete();
  }, [closeSheet, handleDelete]);

  return (
    <Card>
      <CardHeader>
        <DateBadge>
          <DateDay>{badge.day}</DateDay>
          <DateDivider />
          <DateMonth>{badge.month}</DateMonth>
        </DateBadge>
        <CardHeaderRight>
          <Frequency>{frequencyLabel}</Frequency>
          {isCompact ? (
            <MenuWrapper>
              <KebabButton
                size="small"
                onClick={openSheet}
                aria-label="Recurring transaction options"
                aria-haspopup="dialog"
              />
            </MenuWrapper>
          ) : (
            <RecurringCardMenu onEdit={handleEdit} onDelete={handleDelete} />
          )}
        </CardHeaderRight>
      </CardHeader>

      <CardTitle title={title}>{title}</CardTitle>

      <CardFooter>
        <Amount $variant={variant}>{amount}</Amount>
        <CountdownPill $isSoon={countdown.isSoon}>
          {countdown.label}
        </CountdownPill>
      </CardFooter>

      <BottomActionSheet
        isOpen={isSheetOpen}
        onClose={closeSheet}
        ariaLabel="Recurring transaction actions"
      >
        <SheetCard>
          <SheetHeader>
            <HeaderInfo>
              <HeaderTitle>{title}</HeaderTitle>
              <HeaderSubtitle>{sheetSubtitle}</HeaderSubtitle>
            </HeaderInfo>
          </SheetHeader>
          <ActionButton type="button" onClick={onSheetEdit}>
            Edit
          </ActionButton>
          <ActionButton
            type="button"
            $variant="destructive"
            onClick={onSheetDelete}
          >
            Delete
          </ActionButton>
        </SheetCard>
        <SheetCard>
          <CancelButton type="button" onClick={closeSheet}>
            Cancel
          </CancelButton>
        </SheetCard>
      </BottomActionSheet>
    </Card>
  );
};
